import algoliasearch, { SearchClient } from 'algoliasearch/lite';
import { ALGOLIA_APP_ID, ALGOLIA_TOKEN } from '~/services/instantsearch/variables';
/**
 * algoliasearch/lite client, doesn’t offer indexing methods.
 * If you want to perform indexing operations,
 * you have to import the regular algoliasearch client.
 */
export const algoliaClient = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_TOKEN);

/**
 * Wraps an Algolia search client to skip unnecessary requests for empty queries.
 *
 * Empty queries are only allowed for explicitly whitelisted indexes.
 * See [Algolia's guide on conditional requests](https://www.algolia.com/doc/guides/building-search-ui/going-further/conditional-requests/js/) for more details.
 *
 * @param baseClient - The original Algolia search client.
 * @param allowedEmptyQueryIndexes - Indexes allowed to receive empty queries.
 * @returns A modified search client with conditional request filtering.
 */

type AllowedIndexSuffixes = 'price_asc' | 'price_desc' | 'query_suggestions';

const createFilteredSearchClient = (
    baseClient: SearchClient,
    allowedIndexSuffixes: AllowedIndexSuffixes[],
): SearchClient => {
    const isAllowedEmptyQuery = (indexName: string) =>
        allowedIndexSuffixes.some((suffix) => indexName.endsWith(suffix));

    return {
        ...baseClient,
        search(requests) {
            const shouldReturnEmptyResults = requests.every((req) => {
                const query = req.params?.query || '';
                const isEmptyQuery = query.trim() === '';
                const isAllowed = isAllowedEmptyQuery(req.indexName);
                return isEmptyQuery && !isAllowed;
            });

            if (shouldReturnEmptyResults) {
                return Promise.resolve({
                    results: requests.map(() => ({
                        hits: [],
                        nbHits: 0,
                        page: 0,
                        nbPages: 0,
                        hitsPerPage: 0,
                        exhaustiveNbHits: false,
                        query: '',
                        params: '',
                        processingTimeMS: 0,
                    })),
                });
            }

            return baseClient.search(requests);
        },
    };
};

const allowedEmptyQueryIndexes: AllowedIndexSuffixes[] = ['query_suggestions'];

export const filteredAlgoliaClient = createFilteredSearchClient(
    algoliaClient,
    allowedEmptyQueryIndexes,
);
